import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { PlayIcon, RotateCcwIcon } from 'lucide-react';
import { SnakButton } from './SnakButton';
import { PfsCurve } from './PfsCurve';
import { ZETA_REFERENCE, zetaPfs } from '../../data/caprelsa';
type RevealSceneProps = {
  onNext: () => void;
  still?: boolean;
};
const RUN_MS = 3000;
export function RevealScene({
  onNext,
  still = false
}: RevealSceneProps) {
  const target = zetaPfs.axisMax;
  const [month, setMonth] = useState(still ? target : 0);
  const [running, setRunning] = useState(false);
  const raf = useRef<number>();
  const run = () => {
    if (raf.current) cancelAnimationFrame(raf.current);
    setRunning(true);
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / RUN_MS);
      setMonth(t * target);
      if (t < 1) raf.current = requestAnimationFrame(tick);else setRunning(false);
    };
    raf.current = requestAnimationFrame(tick);
  };
  useEffect(() => () => raf.current && cancelAnimationFrame(raf.current), []);
  const done = month >= target - 0.01;
  return <div className="flex h-full flex-col" data-id="element-598" data-mp="2dvh5hyjtu8p2">
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-cap-gold" data-id="element-599" data-mp="293fzgud95e7h">ZETA · pivotal phase III</p>
      <h2 className="mt-1.5 text-[27px] font-extrabold leading-[1.05] tracking-[-0.03em] text-cap-purple" data-id="element-600" data-mp="244mhaon0sz0z">Explore the evidence for disease control.</h2>

      <div className="mt-4 rounded-2xl border-t-4 border-cap-gold bg-white p-3.5 shadow-[0_8px_24px_-12px_rgba(61,39,133,0.25)] ring-1 ring-cap-line" data-id="element-601" data-mp="2042n9txqubj2">
        <div className="flex items-center justify-between" data-id="element-602" data-mp="1q41yvtl0p580">
          <div data-id="element-603" data-mp="1q6o7zxnk3ssc">
            <p className="text-[12px] font-bold text-cap-ink" data-id="element-604" data-mp="1l5idpve8zxgp">Progression-free survival</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-cap-grey" data-id="element-605" data-mp="1l7v5f09t5m6y">Month {Math.min(month, target).toFixed(0).padStart(2, '0')}</p>
          </div>
          <button type="button" onClick={run} disabled={running} className="inline-flex items-center gap-1.5 rounded-full bg-cap-gold px-3.5 py-2 text-[11px] font-bold tracking-[0.08em] text-white transition-[transform,opacity] active:scale-[0.96] disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cap-purple" data-id="element-606" data-mp="3fk1dvrbbhovk">
            {month > 0 && !running ? <RotateCcwIcon size={12} aria-hidden="true" data-id="element-607" data-mp="1wkkbood6lt81" /> : <PlayIcon size={12} className="fill-current" aria-hidden="true" data-id="element-608" data-mp="1wln450q5k3q2" />}
            {month > 0 && !running ? 'REPLAY' : 'RUN THE TRIAL'}
          </button>
        </div>

        <div className="mt-2 h-[196px]" data-id="element-609" data-mp="1njdsg7j5e4b3">
          <PfsCurve month={month} data-id="element-610" data-mp="1nmi6dwmf0eey" />
        </div>

        <div className="mt-1 flex items-center gap-4 text-[10.5px] font-semibold" data-id="element-611" data-mp="1rkft8sspqof6">
          <span className="flex items-center gap-1.5 text-cap-purple" data-id="element-612" data-mp="c4qqodgmj85q">
            <span className="h-[3px] w-4 rounded-full bg-cap-purple" data-id="element-613" data-mp="g6lp9a7677ft" /> Caprelsa 300 mg
          </span>
          <span className="flex items-center gap-1.5 text-[#a07a17]" data-id="element-614" data-mp="8msyq2gu8ods">
            <span className="h-[3px] w-4 rounded-full bg-cap-gold" data-id="element-615" data-mp="8p5pn54kq92z" /> Placebo
          </span>
        </div>

        <motion.div initial={false} animate={{
        opacity: done ? 1 : 0.25
      }} className="mt-3 grid grid-cols-3 gap-2 border-t border-cap-line pt-2.5 text-center" data-id="element-616" data-mp="3qcbdahgqe1r">
          {[{
          v: `HR ${zetaPfs.hazardRatio}`,
          l: 'PFS'
        }, {
          v: zetaPfs.ci.replace('95% CI ', ''),
          l: '95% CI'
        }, {
          v: zetaPfs.p,
          l: 'Primary endpoint'
        }].map(s => <div key={s.l} data-id="element-617" data-mp="3vrim4b1hp6tv">
              <p className="text-[14px] font-extrabold text-cap-purple tabular-nums" data-id="element-618" data-mp="3vsu1770dy10a">{s.v}</p>
              <p className="font-mono text-[8.5px] uppercase tracking-[0.12em] text-cap-grey" data-id="element-619" data-mp="3r0sess9i6twj">{s.l}</p>
            </div>)}
        </motion.div>
      </div>

      <div className="mt-2.5 space-y-1 text-[9px] leading-snug text-cap-grey" data-id="element-620" data-mp="2yceicgy2c5n3">
        <p data-id="element-621" data-mp="2yaah66rjtkqi">
          {zetaPfs.design} · n={zetaPfs.n}. {zetaPfs.population}. {zetaPfs.endpoint}.
        </p>
        <p data-id="element-622" data-mp="21j6mzluiekuo">*{zetaPfs.notes[0]} Curves are schematic, drawn from reported medians — not the published Kaplan–Meier plot.</p>
        <p data-id="element-623" data-mp="2aly1xvmlfyr2">
          {zetaPfs.notes[2]} {ZETA_REFERENCE}
        </p>
      </div>

      <div className="mt-auto pt-3" data-id="element-624" data-mp="2fceg5hy8noeh">
        <SnakButton onClick={onNext} disabled={!done && !still} data-id="element-625" data-mp="2fcob7jy54lr1">
          HOW DOES IT WORK?
        </SnakButton>
      </div>
    </div>;
}